import React, { ChangeEvent, FC, Fragment, useContext, useState } from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    TextField,
    Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import FileDropBoxArea from '../file-drop-box-area/file-drop-box-area.component';
import Loader from '../loader/loader.component';
import axios from 'axios';
import { CONSTANTS } from 'config/constants';
import CurrencyInput from '../currency-input/currency-input.component';
import { Currency } from 'models/country';
import { AppToasterContext } from 'components/shared/app-toaster/app-toaster-context';
import ImportOrderFromExternalSystemDialog from 'components/shared/import-order-from-external-system-dialog/import-order-from-external-system-dialog.component';
import ImportHistoricalOrdersDialog from 'components/shared/import-historical-orders-dialog/import-historical-orders-dialog.component';

const ACTION_PRICE_LIST = 'action-price-list';
const MATERIAL_PRICE_LIST = 'material-price-list';
const IMPORT_ORDERS = 'import-orders';
// DD-3687: the read-only archive, which has its own multi-phase dialog.
const IMPORT_HISTORICAL_ORDERS = 'import-historical-orders';

const buildUploadUrl = (typeOfImport: string, labId = '', priceListName = '', currencyCode = '') => {
    const encodedName = encodeURIComponent(priceListName || '');
    // super-admin-backend's REST API, authenticated by the session cookie.
    return `${CONSTANTS.REST_API_URI}/organizations/${labId}/priceList/${encodedName}/uploads/${typeOfImport}/currency/${currencyCode}`;
};

type ImportFileDialogProps = {
    open: boolean;
    onCloseDialog: () => void;
    labId?: string;
};
const ImportFileDialog: FC<ImportFileDialogProps> = ({ open, onCloseDialog, labId }: ImportFileDialogProps) => {
    const { t: translate } = useTranslation(['common', 'users', 'client-directory', 'orders']);
    const { setAppToasterContent } = useContext(AppToasterContext);
    const [selectedTypeOfImport, setSelectedTypeOfImport] = useState('');
    const [priceListName, setPriceListName] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [currency, setCurrency] = useState<Currency>({ code: '' } as Currency);
    const [stagedFiles, setStagedFiles] = useState<File[]>([]);
    const [uploading, setUploading] = useState(false);
    const [openImportOrders, setOpenImportOrders] = useState(false);
    const [openImportHistoricalOrders, setOpenImportHistoricalOrders] = useState(false);

    const isPriceListImport =
        selectedTypeOfImport === ACTION_PRICE_LIST || selectedTypeOfImport === MATERIAL_PRICE_LIST;

    const isSubmitDisabled = !priceListName || !currency.code || stagedFiles.length === 0;

    const resetForm = () => {
        setSelectedTypeOfImport('');
        setPriceListName('');
        setCurrency({ code: '' } as Currency);
        setStagedFiles([]);
        setErrorMessage('');
    };

    const handleStageFiles = (files: File[]) => {
        setStagedFiles(files ? Array.from(files) : []);
    };

    const handleSubmit = async () => {
        try {
            setUploading(true);
            const formData = new FormData();
            for (const file of stagedFiles) {
                formData.append('files', file, file.name);
            }
            const url = buildUploadUrl(selectedTypeOfImport, labId, priceListName, currency.code);
            const response = await axios.post(url, formData, {
                withCredentials: true,
                // Required by super-admin-backend: forces a CORS preflight before the cookie is used.
                headers: { 'apollo-require-preflight': '1' },
            });
            if (response.status === 200) {
                setAppToasterContent({
                    isOpen: true,
                    severity: 'success',
                    message: translate('users:SUCCESSFULL_IMPORT'),
                });
                handleClose();
            }
        } catch (error) {
            const uploadError = axios.isAxiosError(error) ? error.response?.data?.error : undefined;
            setAppToasterContent({
                isOpen: true,
                severity: 'error',
                message: uploadError || translate('common:AN_ERROR_OCCURRED'),
            });
        } finally {
            setUploading(false);
        }
    };

    const handleSelectTypeOfImportChange = (event: SelectChangeEvent) => {
        const value = event.target.value;
        resetForm();
        setSelectedTypeOfImport(value);
        if (value === IMPORT_ORDERS) {
            setOpenImportOrders(true);
            setSelectedTypeOfImport('');
            onCloseDialog();
        }
        if (value === IMPORT_HISTORICAL_ORDERS) {
            setOpenImportHistoricalOrders(true);
            setSelectedTypeOfImport('');
            onCloseDialog();
        }
    };

    const handlePriceListNameChange = (event: ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setErrorMessage('');
        setPriceListName(value);
    };

    const handleBlur = () => {
        setErrorMessage(priceListName ? '' : 'PRICE_LIST_NAME_MISSING');
    };

    const handleClose = () => {
        setOpenImportOrders(false);
        setOpenImportHistoricalOrders(false);
        resetForm();
        onCloseDialog();
    };

    return (
        <Fragment>
            <Dialog fullWidth maxWidth="lg" open={open} onClose={handleClose}>
                <DialogTitle>{translate('users:IMPORT_INFORMATION')}</DialogTitle>
                <DialogContent>
                    {uploading && <Loader />}
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl margin="dense" fullWidth>
                                <InputLabel>{translate('users:TYPE_OF_IMPORT')}</InputLabel>
                                <Select
                                    value={selectedTypeOfImport}
                                    label={translate('users:TYPE_OF_IMPORT')}
                                    onChange={handleSelectTypeOfImportChange}
                                >
                                    <MenuItem value={MATERIAL_PRICE_LIST}>
                                        {translate('client-directory:MATERIAL_PRICE_LIST')}
                                    </MenuItem>
                                    <MenuItem value={ACTION_PRICE_LIST}>
                                        {translate('client-directory:ACTION_PRICE_LIST')}
                                    </MenuItem>
                                    <MenuItem value={IMPORT_ORDERS}>
                                        {translate('users:IMPORT_ORDERS_FROM_EXTERNAL_SYSTEM')}
                                    </MenuItem>
                                    <MenuItem value={IMPORT_HISTORICAL_ORDERS}>
                                        {translate('orders:HISTORICAL_IMPORT_TITLE')}
                                    </MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {isPriceListImport ? (
                            <Grid item xs={12}>
                                <Grid container spacing={1} alignItems="flex-start">
                                    <Grid item md={6} sm={12}>
                                        <TextField
                                            id="price-list-name"
                                            fullWidth
                                            label={translate('common:NAME')}
                                            required
                                            helperText={errorMessage ? translate(`common:${errorMessage}`) : undefined}
                                            error={!!errorMessage}
                                            value={priceListName}
                                            onBlur={handleBlur}
                                            onChange={handlePriceListNameChange}
                                        />
                                    </Grid>
                                    <Grid item md={6} sm={12}>
                                        <CurrencyInput
                                            currency={currency}
                                            onCurrencySelect={(currencyObj) => {
                                                setCurrency(currencyObj);
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        ) : undefined}
                        {isPriceListImport && (
                            <Grid item xs={12}>
                                <FileDropBoxArea
                                    multiple={selectedTypeOfImport === ACTION_PRICE_LIST}
                                    handleChange={handleStageFiles}
                                />
                                {stagedFiles.length > 0 && (
                                    <Typography variant="body2">
                                        {stagedFiles.map((file) => file.name).join(', ')}
                                    </Typography>
                                )}
                            </Grid>
                        )}
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose} disabled={uploading}>
                        {translate('common:CLOSE')}
                    </Button>
                    {isPriceListImport && (
                        <Button variant="contained" disabled={isSubmitDisabled || uploading} onClick={handleSubmit}>
                            {translate('users:IMPORT')}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>
            {openImportOrders && (
                <ImportOrderFromExternalSystemDialog
                    open={openImportOrders}
                    onCloseDialog={() => setOpenImportOrders(false)}
                    labId={labId}
                />
            )}
            {/* Deliberately not behind `openImportHistoricalOrders &&`: a
                historical import keeps running on the server after the dialog
                is closed, and unmounting would throw away the batch id with it,
                leaving the run unreachable. Mounted, its state survives a close
                so reopening returns to the same batch. */}
            <ImportHistoricalOrdersDialog
                open={openImportHistoricalOrders}
                onCloseDialog={() => setOpenImportHistoricalOrders(false)}
                labId={labId}
            />
        </Fragment>
    );
};

export default ImportFileDialog;
